import { useState } from 'react';
import { WineWidgetModel } from '../../../../models/WineWidgetModel';

interface UseCartQuantityProps {
  wine: WineWidgetModel;
}

/**
 * The `useCartQuantity` function is a custom hook that manages the quantity of items in a cart,
 * allowing for incrementing and decrementing the quantity within the given stock limit.
 * @param wine - The wine object.
 * @returns an object with three properties: `increment`, `decrement`, and `cartQuantity`.
 * The `increment` and `decrement` functions are used to increase and decrease the value of
 * `cartQuantity`, respectively. The `cartQuantity` property represents the quantity of items in the cart.
 */

const useCartQuantity = ({ wine }: UseCartQuantityProps) => {
  const [cartQuantity, setCartQuantity] = useState(wine.minimumCartQuantity ?? 1);

  /**
   * The function `decrement` decreases the value of `cartQuantity` by 1 if it is greater than 1.
   */
  const decrement = () => {
    if (cartQuantity > 1 && cartQuantity > wine.minimumCartQuantity) setCartQuantity((prevState) => prevState - 1);
  };

  /**
   * The function `increment` increases the value of `cartQuantity` by 1 if it is less than `stock`.
   */
  const increment = () => {
    if (cartQuantity < wine.availableQuantity && cartQuantity < wine.maximumCartQuantity) setCartQuantity((prevState) => prevState + 1);
  };

  return { increment, decrement, cartQuantity };
};

export default useCartQuantity;
